import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, Loader2, AlertCircle, Maximize2, Crosshair, Copy } from 'lucide-react';

// Set up PDF.js worker from node_modules
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

/** A highlighted region on a PDF page. Coordinates are normalized 0..1. */
export interface PdfHighlight {
  id: string;
  pageNumber: number; // 1-based
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string | null;
  color?: string | null;
}

/** A rectangle drawn in annotate mode, reported to the parent (normalized 0..1). */
export interface PdfDrawnZone {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  /** OCR text extracted from the drawn region (when available). */
  extractedText?: string;
}

interface PdfViewerProps {
  url: string;
  initialPage?: number;
  initialTextSelectMode?: boolean;
  onLoadError?: () => void;
  onAspectRatioDetected?: (aspectRatio: number, width: number, height: number) => void;
  /** Highlighted regions to render on top of the pages. */
  highlights?: PdfHighlight[];
  /** Currently focused highlight (emphasized + auto-navigates to its page). */
  activeHighlightId?: string | null;
  /** When true, dragging draws a rectangle reported via onDrawZone instead of panning. */
  annotateMode?: boolean;
  /** Called when a zone is drawn in annotate mode (normalized coords). */
  onDrawZone?: (zone: PdfDrawnZone) => void;
  /** Called when an existing highlight is clicked. */
  onHighlightClick?: (id: string) => void;
}

interface PageDimensions {
  width: number;
  height: number;
  aspectRatio: number;
  orientation: 'portrait' | 'landscape' | 'square';
}

/**
 * PdfViewer — continuous scroll document with modifier-key zoom.
 *
 * - Normal scroll: move through all pages vertically (scrollable document).
 * - ⌘/Ctrl + scroll: zoom in/out around the cursor.
 */
export function PdfViewer({
  url,
  initialPage,
  initialTextSelectMode,
  onLoadError,
  onAspectRatioDetected,
  highlights,
  activeHighlightId,
  annotateMode = false,
  onDrawZone,
  onHighlightClick,
}: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(initialPage || 1);
  const [rotation, setRotation] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pageDimensions, setPageDimensions] = useState<PageDimensions | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);
  
  const [scale, setScale] = useState<number>(1);

  // OCR text selection state
  const [textSelectMode, setTextSelectMode] = useState(initialTextSelectMode ?? false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const selectionStart = useRef<{ x: number; y: number; pageIndex: number } | null>(null);
  const [selectionPageIndex, setSelectionPageIndex] = useState<number | null>(null);
  const tesseractWorkerRef = useRef<any>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lastUrlRef = useRef<string>('');
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const scrollSyncRafRef = useRef<number | null>(null);
  
  // Zoom constants
  const MIN_SCALE = 0.25;
  const MAX_SCALE = 4;
  const ZOOM_SENSITIVITY = 0.002;

  // Reset state when URL changes (new PDF or refreshed URL)
  useEffect(() => {
    if (url !== lastUrlRef.current) {
      lastUrlRef.current = url;
      setNumPages(0);
      setPageNumber(initialPage || 1);
      setScale(1);
      setRotation(0);
      pageRefs.current = [];
      setSelectionPageIndex(null);
      setLoading(true);
      setError(false);
      setPageDimensions(null);
    }
  }, [url]);

  // Set up ResizeObserver to track container size changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initial size measurement
    const updateContainerSize = () => {
      const rect = container.getBoundingClientRect();
      setContainerSize({
        width: rect.width - 32, // Account for padding (p-4 = 16px * 2)
        height: rect.height - 32,
      });
    };

    updateContainerSize();

    // Watch for resize
    resizeObserverRef.current = new ResizeObserver(() => {
      updateContainerSize();
    });
    resizeObserverRef.current.observe(container);

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  function onDocumentLoadSuccess({ numPages: loadedPages }: { numPages: number }) {
    setNumPages(loadedPages);
    pageRefs.current = new Array(loadedPages).fill(null);
    setLoading(false);
    setError(false);
  }

  function onPageLoadSuccess(page: any) {
    // Get the PDF's native dimensions (before any rotation)
    const nativeWidth = page.width;
    const nativeHeight = page.height;
    
    // Calculate aspect ratio considering rotation
    // When rotated 90 or 270 degrees, width and height are swapped
    const isRotated = rotation === 90 || rotation === 270;
    const effectiveWidth = isRotated ? nativeHeight : nativeWidth;
    const effectiveHeight = isRotated ? nativeWidth : nativeHeight;
    
    const aspectRatio = effectiveWidth / effectiveHeight;
    const orientation: 'portrait' | 'landscape' | 'square' = 
      aspectRatio > 1.05 ? 'landscape' : 
      aspectRatio < 0.95 ? 'portrait' : 
      'square';
    
    const dimensions: PageDimensions = {
      width: effectiveWidth,
      height: effectiveHeight,
      aspectRatio,
      orientation,
    };
    
    setPageDimensions(dimensions);
    
    // Notify parent component of detected aspect ratio
    onAspectRatioDetected?.(aspectRatio, effectiveWidth, effectiveHeight);
    
    console.log('📄 PDF Page Loaded:', {
      nativeWidth,
      nativeHeight,
      rotation,
      effectiveWidth,
      effectiveHeight,
      aspectRatio: aspectRatio.toFixed(3),
      orientation,
    });
  }

  function onDocumentLoadError(error: Error) {
    console.error('Error loading PDF:', error);
    setLoading(false);
    setError(true);
    onLoadError?.();
  }

  const MIN_RENDER_WIDTH = 800;

  const getFitWidth = useCallback((): number => {
    if (!containerSize) return MIN_RENDER_WIDTH;
    return Math.floor(Math.min(2000, Math.max(MIN_RENDER_WIDTH, containerSize.width - 32)));
  }, [containerSize]);

  const getPageWidth = useCallback((): number => {
    return Math.floor(getFitWidth() * scale);
  }, [getFitWidth, scale]);

  const getPageHeight = useCallback((): number => {
    return pageDimensions
      ? getPageWidth() / pageDimensions.aspectRatio
      : getPageWidth() * 1.4;
  }, [getPageWidth, pageDimensions]);

  const scrollToPage = useCallback((targetPage: number) => {
    const index = targetPage - 1;
    const pageEl = pageRefs.current[index];
    const container = containerRef.current;
    setPageNumber(targetPage);
    if (!pageEl || !container) return;
    container.scrollTo({ top: Math.max(0, pageEl.offsetTop - 16), behavior: 'smooth' });
  }, []);

  const updatePageFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return;
    const scrollMid = container.scrollTop + container.clientHeight * 0.3;
    let bestPage = 1;
    for (let i = 0; i < numPages; i++) {
      const el = pageRefs.current[i];
      if (!el) continue;
      if (el.offsetTop <= scrollMid) bestPage = i + 1;
    }
    setPageNumber(bestPage);
  }, [numPages]);

  const handleScroll = useCallback(() => {
    if (scrollSyncRafRef.current != null) return;
    scrollSyncRafRef.current = requestAnimationFrame(() => {
      scrollSyncRafRef.current = null;
      updatePageFromScroll();
    });
  }, [updatePageFromScroll]);

  const zoomAroundPoint = useCallback((newScale: number, cursorX: number, cursorY: number) => {
    const container = containerRef.current;
    if (!container) {
      setScale(newScale);
      return;
    }
    const oldScale = scale;
    const contentX = (container.scrollLeft + cursorX) / oldScale;
    const contentY = (container.scrollTop + cursorY) / oldScale;
    setScale(newScale);
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      containerRef.current.scrollLeft = contentX * newScale - cursorX;
      containerRef.current.scrollTop = contentY * newScale - cursorY;
    });
  }, [scale]);

  useEffect(() => {
    if (!loading && numPages > 0 && initialPage && initialPage > 1) {
      requestAnimationFrame(() => scrollToPage(initialPage));
    }
  }, [loading, numPages, initialPage, scrollToPage]);

  const goToPrevPage = () => scrollToPage(Math.max(1, pageNumber - 1));
  const goToNextPage = () => scrollToPage(Math.min(numPages, pageNumber + 1));

  const handleZoomIn = () => {
    const container = containerRef.current;
    const cursorX = container ? container.clientWidth / 2 : 0;
    const cursorY = container ? container.clientHeight / 2 : 0;
    zoomAroundPoint(Math.min(MAX_SCALE, scale * 1.2), cursorX, cursorY);
  };

  const handleZoomOut = () => {
    const container = containerRef.current;
    const cursorX = container ? container.clientWidth / 2 : 0;
    const cursorY = container ? container.clientHeight / 2 : 0;
    zoomAroundPoint(Math.max(MIN_SCALE, scale / 1.2), cursorX, cursorY);
  };

  const handleResetZoom = useCallback(() => {
    setScale(1);
  }, []);
  
  const rotate = () => {
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
    handleResetZoom();
  };

  // =========================================================================
  // OCR: Tesseract worker + text extraction
  // =========================================================================

  const getTesseractWorker = useCallback(async () => {
    if (tesseractWorkerRef.current) return tesseractWorkerRef.current;
    const Tesseract = await import('tesseract.js');
    const worker = await Tesseract.createWorker('eng');
    tesseractWorkerRef.current = worker;
    return worker;
  }, []);

  useEffect(() => {
    return () => {
      if (tesseractWorkerRef.current) {
        tesseractWorkerRef.current.terminate();
        tesseractWorkerRef.current = null;
      }
    };
  }, []);

  const findPageAtPoint = useCallback((clientX: number, clientY: number): number | null => {
    for (let i = 0; i < pageRefs.current.length; i++) {
      const pageEl = pageRefs.current[i];
      if (!pageEl) continue;
      const rect = pageEl.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
        return i;
      }
    }
    return null;
  }, []);

  const getCoordsOnPage = useCallback((clientX: number, clientY: number, pageIndex: number): { x: number; y: number } | null => {
    const pageEl = pageRefs.current[pageIndex];
    if (!pageEl) return null;
    const rect = pageEl.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const extractTextFromSelection = useCallback(async (
    rect: { x: number; y: number; w: number; h: number },
    pageIndex: number,
  ) => {
    const pageDiv = pageRefs.current[pageIndex];
    if (!pageDiv) return '';
    const canvas = pageDiv.querySelector('canvas');
    if (!canvas) return '';

    const pageRenderWidth = getPageWidth();
    const canvasScaleX = canvas.width / pageRenderWidth;
    const canvasScaleY = canvas.height / (pageDimensions ? pageRenderWidth / pageDimensions.aspectRatio : canvas.height);

    const cropX = Math.round(Math.max(0, rect.x) * canvasScaleX);
    const cropY = Math.round(Math.max(0, rect.y) * canvasScaleY);
    const cropW = Math.round(rect.w * canvasScaleX);
    const cropH = Math.round(rect.h * canvasScaleY);

    if (cropW < 2 || cropH < 2) return '';

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = cropW;
    tempCanvas.height = cropH;
    const ctx = tempCanvas.getContext('2d');
    if (!ctx) return '';
    ctx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    try {
      const worker = await getTesseractWorker();
      const { data } = await worker.recognize(tempCanvas);
      return (data.text || '').trim();
    } catch (err) {
      console.warn('Tesseract OCR failed:', err);
      return '';
    }
  }, [getPageWidth, pageDimensions, getTesseractWorker]);

  // =========================================================================
  // OCR: Selection mouse handlers
  // =========================================================================

  const handleSelectionMouseDown = useCallback((e: React.MouseEvent) => {
    if ((!textSelectMode && !annotateMode) || e.button !== 0) return;
    const pageIndex = findPageAtPoint(e.clientX, e.clientY);
    if (pageIndex == null) return;
    const coords = getCoordsOnPage(e.clientX, e.clientY, pageIndex);
    if (!coords) return;
    e.preventDefault();
    e.stopPropagation();
    selectionStart.current = { ...coords, pageIndex };
    setSelectionPageIndex(pageIndex);
    setPageNumber(pageIndex + 1);
    setIsSelecting(true);
    setSelectionRect({ x: coords.x, y: coords.y, w: 0, h: 0 });
    setExtractedText(null);
  }, [textSelectMode, annotateMode, findPageAtPoint, getCoordsOnPage]);

  const handleSelectionMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isSelecting || !selectionStart.current) return;
    const coords = getCoordsOnPage(e.clientX, e.clientY, selectionStart.current.pageIndex);
    if (!coords) return;
    e.preventDefault();
    const start = selectionStart.current;
    setSelectionRect({
      x: Math.min(start.x, coords.x),
      y: Math.min(start.y, coords.y),
      w: Math.abs(coords.x - start.x),
      h: Math.abs(coords.y - start.y),
    });
  }, [isSelecting, getCoordsOnPage]);

  const handleSelectionMouseUp = useCallback(async (e: React.MouseEvent) => {
    if (!isSelecting || !selectionRect) return;
    e.preventDefault();
    setIsSelecting(false);
    const pageIndex = selectionStart.current?.pageIndex ?? selectionPageIndex ?? pageNumber - 1;
    selectionStart.current = null;

    if (selectionRect.w < 5 || selectionRect.h < 5) {
      setSelectionRect(null);
      setSelectionPageIndex(null);
      return;
    }

    setIsOcrProcessing(true);
    setExtractedText('Recognizing...');
    try {
      const text = await extractTextFromSelection(selectionRect, pageIndex);
      if (text) {
        setExtractedText(text);
        navigator.clipboard.writeText(text).then(() => {
          setCopiedValue(text);
          setTimeout(() => setCopiedValue(null), 2500);
        });
      } else {
        setExtractedText('(no text found in selection)');
      }
    } catch {
      setExtractedText('(OCR failed)');
    } finally {
      setIsOcrProcessing(false);
    }
  }, [isSelecting, selectionRect, selectionPageIndex, pageNumber, extractTextFromSelection]);

  // Annotate mode: finish drawing a zone, report normalized coords to parent.
  const handleZoneMouseUp = useCallback(async (e: React.MouseEvent) => {
    if (!isSelecting || !selectionRect) return;
    e.preventDefault();
    setIsSelecting(false);
    const pageIndex = selectionStart.current?.pageIndex ?? selectionPageIndex ?? pageNumber - 1;
    selectionStart.current = null;

    if (selectionRect.w < 5 || selectionRect.h < 5) {
      setSelectionRect(null);
      setSelectionPageIndex(null);
      return;
    }

    const renderWidth = getPageWidth();
    const renderHeight = getPageHeight();
    const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
    const zone: PdfDrawnZone = {
      pageNumber: pageIndex + 1,
      x: clamp01(selectionRect.x / renderWidth),
      y: clamp01(selectionRect.y / renderHeight),
      width: clamp01(selectionRect.w / renderWidth),
      height: clamp01(selectionRect.h / renderHeight),
    };

    try {
      const extractedText = await extractTextFromSelection(selectionRect, pageIndex);
      if (extractedText) zone.extractedText = extractedText;
    } catch {
      // OCR is best-effort for annotation linking.
    }

    onDrawZone?.(zone);
    setSelectionRect(null);
    setSelectionPageIndex(null);
  }, [isSelecting, selectionRect, selectionPageIndex, pageNumber, getPageWidth, getPageHeight, onDrawZone, extractTextFromSelection]);

  // Auto-navigate to the page of the active highlight.
  useEffect(() => {
    if (!activeHighlightId || !highlights) return;
    const hl = highlights.find((h) => h.id === activeHighlightId);
    if (hl) scrollToPage(hl.pageNumber);
  }, [activeHighlightId, highlights, scrollToPage]);

  // Wheel handler — ⌘/Ctrl + scroll zooms; normal scroll moves through pages
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!e.metaKey && !e.ctrlKey) return;

    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    const delta = -e.deltaY * ZOOM_SENSITIVITY;
    const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + delta * scale));
    if (newScale === scale) return;

    zoomAroundPoint(newScale, cursorX, cursorY);
  }, [scale, zoomAroundPoint]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (textSelectMode || annotateMode) {
      handleSelectionMouseDown(e);
    }
  }, [textSelectMode, annotateMode, handleSelectionMouseDown]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if ((textSelectMode || annotateMode) && isSelecting) {
      handleSelectionMouseMove(e);
    }
  }, [textSelectMode, annotateMode, isSelecting, handleSelectionMouseMove]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (annotateMode && isSelecting) {
      handleZoneMouseUp(e);
      return;
    }
    if (textSelectMode && isSelecting) {
      handleSelectionMouseUp(e);
    }
  }, [textSelectMode, annotateMode, isSelecting, handleSelectionMouseUp, handleZoneMouseUp]);

  // Attach wheel listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (scrollSyncRafRef.current != null) {
        cancelAnimationFrame(scrollSyncRafRef.current);
      }
    };
  }, [handleWheel]);

  // Keyboard navigation: Shift+Arrow for pages, +/- for zoom
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      // Shift + Arrow keys for page navigation
      if (e.shiftKey) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            scrollToPage(Math.max(1, pageNumber - 1));
            return;
          case 'ArrowRight':
            e.preventDefault();
            scrollToPage(Math.min(numPages || pageNumber, pageNumber + 1));
            return;
        }
      }

      // Zoom shortcuts (no modifier required)
      switch (e.key) {
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          handleResetZoom();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [numPages, pageNumber, scale, scrollToPage]);

  // Format aspect ratio for display
  const formatAspectRatio = (ratio: number): string => {
    // Common aspect ratios
    if (Math.abs(ratio - 0.707) < 0.02) return 'A4 Portrait';
    if (Math.abs(ratio - 1.414) < 0.02) return 'A4 Landscape';
    if (Math.abs(ratio - 0.773) < 0.02) return 'Letter Portrait';
    if (Math.abs(ratio - 1.294) < 0.02) return 'Letter Landscape';
    if (Math.abs(ratio - 1) < 0.05) return 'Square';
    
    // Generic description
    return ratio > 1 ? 'Landscape' : 'Portrait';
  };

  // Calculate zoom percentage for display
  const getZoomPercentage = (): string => {
    return `${Math.round(scale * 100)}%`;
  };

  const pageRenderHeight = pageDimensions
    ? getPageWidth() / pageDimensions.aspectRatio
    : getPageWidth() * 1.4;

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center justify-between bg-gray-900 px-4 py-2 border-b border-gray-700">
        {/* Page Navigation */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-300 min-w-[80px] text-center">
              {loading ? '...' : `${pageNumber} / ${numPages}`}
            </span>
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          {/* Aspect Ratio Info */}
          {pageDimensions && !loading && (
            <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
              {getPageWidth()} × {Math.round(getPageWidth() / pageDimensions.aspectRatio)} · {formatAspectRatio(pageDimensions.aspectRatio)}
            </div>
          )}
          
          <div className="text-xs text-gray-400 dark:text-gray-500 hidden md:block">
            {textSelectMode ? 'Draw a rectangle to extract text' : 'Scroll to navigate • ⌘ scroll to zoom'}
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleZoomOut}
            disabled={scale <= MIN_SCALE}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <button
            onClick={handleResetZoom}
            className="text-sm text-gray-300 hover:text-white min-w-[60px] text-center px-2 py-1 hover:bg-gray-800 rounded transition-colors"
            title="Reset zoom to fit"
          >
            {scale === 1 ? 'Fit' : getZoomPercentage()}
          </button>
          <button
            onClick={handleZoomIn}
            disabled={scale >= MAX_SCALE}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <div className="w-px h-6 bg-gray-700 mx-1" />
          <button
            onClick={handleResetZoom}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-white transition-colors"
            title="Fit to container"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
          <button
            onClick={rotate}
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-white transition-colors"
            title="Rotate 90°"
          >
            <RotateCw className="w-5 h-5" />
          </button>
          <div className="w-px h-6 bg-gray-700 mx-1" />
          <button
            onClick={() => {
              setTextSelectMode(!textSelectMode);
              if (textSelectMode) {
                setSelectionRect(null);
                setExtractedText(null);
                setIsSelecting(false);
              }
            }}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
              textSelectMode
                ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
            }`}
            title={textSelectMode ? 'Exit text selection mode' : 'Draw a rectangle to extract text via OCR'}
          >
            <Crosshair className="w-3.5 h-3.5" />
            {textSelectMode ? 'Select ON' : 'Select'}
          </button>
          {copiedValue && (
            <div className="flex items-center gap-1 text-xs text-cyan-300 animate-pulse">
              <Copy className="w-3 h-3" />
              Copied!
            </div>
          )}
        </div>
      </div>

      {/* PDF Display — continuous scroll document */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-auto bg-gray-900 ${textSelectMode || annotateMode ? 'cursor-crosshair' : ''}`}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {error ? (
          <div className="flex items-center justify-center h-full min-h-[16rem] text-center text-gray-400 dark:text-gray-500">
            <div>
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400 opacity-50" />
              <p className="text-lg">Failed to load PDF</p>
              <p className="text-sm mt-2">The document may be corrupted or temporarily unavailable</p>
            </div>
          </div>
        ) : (
          <div className="p-4 min-w-min mx-auto">
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="text-center text-gray-400 dark:text-gray-500 py-16">
                  <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" />
                  <p>Loading PDF...</p>
                </div>
              }
            >
              {numPages > 0 &&
                Array.from({ length: numPages }, (_, index) => {
                  const pn = index + 1;
                  const pageHighlights = (highlights ?? []).filter((h) => h.pageNumber === pn);
                  const showSelectionOverlay =
                    textSelectMode &&
                    selectionRect &&
                    selectionPageIndex === index &&
                    selectionRect.w > 2 &&
                    selectionRect.h > 2;
                  const showDrawRect =
                    annotateMode &&
                    selectionRect &&
                    selectionPageIndex === index &&
                    selectionRect.w > 2 &&
                    selectionRect.h > 2;

                  return (
                    <div
                      key={`page-${pn}`}
                      ref={(el) => {
                        pageRefs.current[index] = el;
                      }}
                      className="relative mx-auto mb-4 last:mb-0 shadow-2xl"
                      style={{ width: getPageWidth(), lineHeight: 0 }}
                    >
                      <Page
                        pageNumber={pn}
                        width={getPageWidth()}
                        rotate={rotation}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        onLoadSuccess={pn === 1 ? onPageLoadSuccess : undefined}
                        loading={
                          <div
                            className="flex items-center justify-center bg-gray-800"
                            style={{ width: getPageWidth(), height: pageRenderHeight }}
                          >
                            <Loader2 className="w-8 h-8 text-gray-400 dark:text-gray-500 animate-spin" />
                          </div>
                        }
                      />

                      {showSelectionOverlay && selectionRect && (
                        <svg
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: getPageWidth(),
                            height: pageRenderHeight,
                            pointerEvents: 'none',
                          }}
                        >
                          <rect
                            x={selectionRect.x}
                            y={selectionRect.y}
                            width={selectionRect.w}
                            height={selectionRect.h}
                            fill={isOcrProcessing ? 'rgba(234,179,8,0.15)' : extractedText ? 'rgba(6,182,212,0.15)' : 'rgba(6,182,212,0.1)'}
                            stroke={isOcrProcessing ? 'rgba(234,179,8,0.9)' : extractedText ? 'rgba(6,182,212,0.9)' : 'rgba(6,182,212,0.7)'}
                            strokeWidth={1.5 / scale}
                            strokeDasharray={isSelecting ? '4,3' : isOcrProcessing ? '6,3' : 'none'}
                            rx={2}
                          />
                          {(isOcrProcessing || extractedText) && (
                            <foreignObject
                              x={Math.max(0, selectionRect.x)}
                              y={Math.max(0, selectionRect.y - 28)}
                              width={Math.max(selectionRect.w, 300)}
                              height={26}
                            >
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                background: isOcrProcessing ? 'rgba(234,179,8,0.9)' : 'rgba(6,182,212,0.9)',
                                color: isOcrProcessing ? '#000' : '#fff',
                                fontSize: 11,
                                fontWeight: 600,
                                padding: '3px 10px',
                                borderRadius: 4,
                                whiteSpace: 'nowrap',
                                maxWidth: Math.max(selectionRect.w, 300),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                              }}>
                                {isOcrProcessing ? '⏳ Recognizing...' : `✓ Copied: ${extractedText}`}
                              </div>
                            </foreignObject>
                          )}
                        </svg>
                      )}

                      {(pageHighlights.length > 0 || showDrawRect) && (
                        <svg
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: getPageWidth(),
                            height: pageRenderHeight,
                            pointerEvents: 'none',
                          }}
                        >
                          {pageHighlights.map((h) => {
                            const isActive = h.id === activeHighlightId;
                            const stroke = h.color || (isActive ? 'rgba(37,99,235,0.95)' : 'rgba(234,88,12,0.85)');
                            const rw = getPageWidth();
                            const rh = pageRenderHeight;
                            return (
                              <g key={h.id}>
                                <rect
                                  x={h.x * rw}
                                  y={h.y * rh}
                                  width={h.width * rw}
                                  height={h.height * rh}
                                  fill={isActive ? 'rgba(37,99,235,0.18)' : 'rgba(234,88,12,0.12)'}
                                  stroke={stroke}
                                  strokeWidth={(isActive ? 2.5 : 1.5) / scale}
                                  rx={2}
                                  style={{ pointerEvents: onHighlightClick ? 'auto' : 'none', cursor: onHighlightClick ? 'pointer' : 'default' }}
                                  onClick={(ev) => {
                                    if (!onHighlightClick) return;
                                    ev.stopPropagation();
                                    onHighlightClick(h.id);
                                  }}
                                />
                                {h.label && (
                                  <foreignObject
                                    x={h.x * rw}
                                    y={Math.max(0, h.y * rh - 22)}
                                    width={Math.max(h.width * rw, 160)}
                                    height={20}
                                  >
                                    <div style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      background: isActive ? 'rgba(37,99,235,0.95)' : 'rgba(234,88,12,0.9)',
                                      color: '#fff',
                                      fontSize: 10,
                                      fontWeight: 600,
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      whiteSpace: 'nowrap',
                                      maxWidth: Math.max(h.width * rw, 160),
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}>
                                      {h.label}
                                    </div>
                                  </foreignObject>
                                )}
                              </g>
                            );
                          })}
                          {showDrawRect && selectionRect && (
                            <rect
                              x={selectionRect.x}
                              y={selectionRect.y}
                              width={selectionRect.w}
                              height={selectionRect.h}
                              fill="rgba(37,99,235,0.15)"
                              stroke="rgba(37,99,235,0.9)"
                              strokeWidth={1.5 / scale}
                              strokeDasharray="4,3"
                              rx={2}
                            />
                          )}
                        </svg>
                      )}
                    </div>
                  );
                })}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}
